import { CheckIcon, ChevronDownIcon, PhoneIcon } from 'lucide-react';
import type { ComponentProps } from 'react';
import React from 'react';
// eslint-disable-next-line import/no-namespace
import * as RPNInput from 'react-phone-number-input';
import flags from 'react-phone-number-input/flags';
import type { InputProps } from './input';
import { Input } from './input';
import { cn } from './helpers';
import {
  Command,
  CommandInput,
  CommandEmpty,
  CommandItem,
  CommandList,
} from './command';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from './dropdown-menu';
import { Label } from './label';
import { cva, type VariantProps } from 'class-variance-authority';

const phoneNumberVariants = cva('flex rounded-md', {
  variants: {
    size: {
      lg: 'h-[48px]',
      sm: 'h-[40px]',
    },
  },
  defaultVariants: {
    size: 'lg',
  },
});

export function PhoneNumberInput({
  className,
  label,
  helperText,
  size,
  ...props
}: ComponentProps<typeof RPNInput.default> &
  VariantProps<typeof phoneNumberVariants> & {
    label?: string;
    helperText?: string;
  }) {
  return (
    <div className='flex flex-col gap-0'>
      {label ? <Label className='mb-3'>{label}</Label> : null}

      <RPNInput.default
        className={cn(phoneNumberVariants({ size, className }))}
        international
        flagComponent={FlagComponent}
        countrySelectComponent={CountrySelect}
        inputComponent={PhoneInput}
        placeholder='Enter phone number'
        {...props}
      />
      {helperText && (
        <p className='mt-1.5 text-xs text-gray-500'>{helperText}</p>
      )}
    </div>
  );
}

const PhoneInput = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, ...props }, ref) => {
    return (
      <Input
        ref={ref}
        data-slot='phone-input'
        className={cn(
          '-ms-px max-w-[auto] h-full flex-1 rounded-s-none shadow-none focus-visible:z-10',
          className
        )}
        {...props}
      />
    );
  }
);
PhoneInput.displayName = 'PhoneInput';

type CountrySelectProps = {
  disabled?: boolean;
  value: RPNInput.Country;
  onChange: (value: RPNInput.Country) => void;
  options: { label: string; value: RPNInput.Country | undefined }[];
};
// gap-2 h-12 data-[state=open]:border-ring data-[state=open]:ring-ring/20 data-[state=open]:ring-[3px] focus-within:border-ring focus-within:ring-ring/20 rounded-r-none w-fit hover:bg-primary-30 hover:text-gray-900 has-aria-invalid:border-destructive/60 has-aria-invalid:ring-destructive/20 dark:has-aria-invalid:ring-destructive/40 relative inline-flex items-center self-stretch rounded-s-md border ps-3 pe-2 transition-[color,box-shadow] outline-none focus-within:z-10 focus-within:ring-[3px] has-disabled:pointer-events-none has-disabled:opacity-50

const CountrySelect = ({
  disabled,
  value,
  onChange,
  options,
}: CountrySelectProps) => {
  const [open, setOpen] = React.useState(false);

  return (
    <DropdownMenu
      open={open}
      onOpenChange={setOpen}
    >
      <DropdownMenuTrigger
        asChild
        disabled={disabled}
      >
        <button
          type='button'
          className='flex disabled:cursor-not-allowed h-full disabled:opacity-50 bg-white cursor-pointer disabled:hover:text-gray-400 border px justify-center items-center border-pale-border rounded-l-lg text-gray-400 hover:text-gray-800 w-fit rounded-r-none gap-2 ps-3 pe-2 h-12 hover:bg-primary-30  transition-[color,box-shadow]  has-aria-invalid:border-destructive/60 has-aria-invalid:ring-destructive/20 focus-within:z-10 focus:ring-ring  has-disabled:pointer-events-none has-disabled:opacity-50'
        >
          <FlagComponent
            country={value}
            countryName={value}
            aria-hidden='true'
          />
          <ChevronDownIcon size={20} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align='start'
        className='p-2 rounded-2xl w-[300px]'
      >
        <Command aria-label='Select country'>
          <CommandInput placeholder='Search countries' />
          <CommandList>
            <CommandEmpty>No countries found</CommandEmpty>
            {options
              .filter((x) => x.value)
              .map((option, i) => {
                const isSelected = value === option.value;
                return (
                  <CommandItem
                    onSelect={() => {
                      onChange(option.value as RPNInput.Country);
                      setOpen(false);
                    }}
                    key={option.value ?? `empty-${i}`}
                    value={option.label!}
                    className={cn(
                      'w-full',
                      isSelected && 'bg-primary-50 text-primary-1000'
                    )}
                  >
                    <span className='text-start flex cursor-pointer w-full justify-between'>
                      <span>
                        {option.label}{' '}
                        {option.value &&
                          `+${RPNInput.getCountryCallingCode(option.value)}`}
                      </span>
                      {isSelected && <CheckIcon />}
                    </span>
                  </CommandItem>
                );
              })}
          </CommandList>
        </Command>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const FlagComponent = ({ country, countryName }: RPNInput.FlagProps) => {
  const Flag = flags[country];

  return (
    <span className='w-5 overflow-hidden rounded-sm'>
      {Flag ? (
        <Flag title={countryName} />
      ) : (
        <PhoneIcon
          size={16}
          aria-hidden='true'
        />
      )}
    </span>
  );
};
